/** @jsx jsx */
// External libraries
import { Suspense, useEffect } from 'react';
import { ThemeProvider } from 'emotion-theming';
import { jsx } from '@emotion/core';
import { Route, Routes, useLocation, useParams } from 'react-router-dom';

// Internal modules
import { getTheme } from '@/styles';
import { useStoreState } from '@/store';
import { WPSLazyReceiptPage } from '../Receipt';
import {
  CI,
  getGlobals,
  logger,
  useEndAndStartCi,
  useMountLogging,
} from '@/lib';
import { LazyApplePayReceipt, LazyLanding } from '@/components/Landing';
// TODO: replace this with LazyBilling with an elmo experiment along with
// changes to webpack config on chunk size to reduce overall bundle size
import * as BillingComponent from '@/components/Billing/Billing';
import { runHealthCheck } from '@/lib/health-check';
import { LoadingSpinnerFromIndexFile } from './LoadingSpinnerFromIndexFile';
import { useInitXprops } from '@/hooks';

const App = (): JSX.Element => {
  useEndAndStartCi({
    end: CI.Event.START_APPLICATION,
    endStatus: CI.Status.Ok,
    start: CI.Event.APP_ROUTING,
  });
  useMountLogging('App');
  useInitXprops();
  const { pathname: locationPathname = '' } = useLocation();

  // Should show WPS Receipt Page
  const isWPSDonePageOpen = useStoreState(({ ui }) => ui.isWPSDonePageOpen);

  // Should show WPS receipt or billing page components
  const Component = isWPSDonePageOpen
    ? WPSLazyReceiptPage
    : BillingOrLazyBilling;

  useEffect(() => {
    // location variable should be available at this line
    logger.info('react_router_current_location', {
      browserPath: window.location.pathname,
      path: locationPathname,
    });
    // TODO: remove this flush event after VIP page ramp
    logger.flush();

    // This healthcheck is a fallback in case Billing|Landing|Receipt
    // healthcheck trigger fails or the component never got loaded
    try {
      setTimeout(
        () =>
          runHealthCheck({
            event: 'reactMounted',
            eventSrc: 'app.tsx',
          }),
        // should be lower than index.html health-check timeout
        // index.html health-check is expected to run only if this one never runs
        3000,
      );
    } catch (err) {
      const { message, name, stack } =
        err instanceof Error ? err : ({} as Partial<Error>);
      logger.error('ERROR_LOGGING_HEALTHCHECK', { message, name, stack });
    }
  }, [locationPathname]);

  return (
    <Suspense fallback={<LoadingSpinnerFromIndexFile />}>
      <ThemeProvider theme={getTheme()}>
        <Routes>
          {/*
           * In case of serverside redirect from router level, we will be
           * calling window.history.pushState in index.jsx file, which
           * updates the browser url before this route resolution happens
           */}
          <Route path="/checkoutweb/landing" element={<LazyLanding />} />
          <Route
            path="/checkoutweb/receipt"
            element={<LazyApplePayReceipt />}
          />
          {/**
           * This can be <Billing /> or <LazyBilling /> based on
           * webpack config we used to create the build
           * webpack.config.common has a replace plugin that controls this
           */}
          <Route path="/checkoutweb/signup" element={<Component />} />
          {/*
           * Default route - This should ALWAYS be the last route & catch all
           * Billing should always be rendered if we are unsure of page.
           * For example SPB can load our app with /checkoutnow route
           */}
          <Route path="/*" element={<FallbackToBilling />} />
        </Routes>
      </ThemeProvider>
    </Suspense>
  );
};

/**
 * Based on the component that is imported we return the
 * right render component from here
 * NOTE: webpack config replaces /components/Billing import with
 * /components/Billing/Billing when we are doing normal build
 * @returns Billing or LazyBilling component
 */
const BillingOrLazyBilling = () => {
  // @ts-expect-error
  const { LazyBilling, Billing } = BillingComponent;
  return LazyBilling ? <LazyBilling /> : <Billing />;
};

/**
 * Logs to Cal and Sfx when the catch_all route is used to render the billing
 * page. We want to monitor this because this caused a "blank page" outage
 * last time (before we had client catchAll route)
 */
const FallbackToBilling = () => {
  useMountLogging('FallbackToBilling');

  const params = useParams();
  const route = getGlobals()?.route || { path: '', requestType: '' };

  // route variable is coming from server as part of initial data
  const isIWCLandingPage = route.path.includes('/checkoutweb/landing');
  const isInternalRedirect = route.requestType === 'REDIRECT';
  const isFallbackIWCLanding = isIWCLandingPage && isInternalRedirect;

  // We do not expect isFallbackIWCLanding value to change at all as
  // the values for isIWCLandingpage and isInternalRedirect is coming
  // from server as part of initial data.
  // If for some reason we see two of the warning log here, then we
  // need to investigate to resolve the issue
  useEffect(() => {
    logger.metric('GUEST_SIGNUP.CatchAll_Route.render');
    logger.warn(
      `falling_back_to_catch_all_client_route_${
        isFallbackIWCLanding ? 'LazyLanding' : 'Billing'
      }`,
      {
        attemptedRoute: params?.['*'],
        isIWCLandingPage: isFallbackIWCLanding,
        referrer: document.referrer,
        sfxMetric: 'GUEST_SIGNUP.CatchAll_Route.render',
        src: 'weasley/app.tsx',
      },
    );
  }, [isFallbackIWCLanding]);

  /**
   * isFallbackIWCLanding check - in case the history.pushState in index.jsx
   * of weasley app is not complete or fails due to lack of browser
   * support by the time route runs here, make sure for /landing path on
   * server we load LazyLanding. If we do not do this, then the other
   * component will not have enough initial data to load the page
   */
  return isFallbackIWCLanding ? <LazyLanding /> : <BillingOrLazyBilling />;
};

export { App };
